/**
 * R128: WotLK 3.3.5a world data loader.
 *
 * Loads quest/creature/spawn/item/spell data extracted from cmangos WotLK 3.3.5a DB
 * (renamed via dictionary in `_references/cmangos-classic/extract-vanilla-world.py`).
 *
 * Files live in `/public/data/vanilla/` so they're streamed at runtime, not
 * bundled into JS (saves ~23 MB from main bundle).
 */

export interface VQuest {
  id: string;
  zone: string;
  zone_full: string;
  level: number;
  minLevel: number;
  title: string;
  details: string | null;
  objectives_text: string | null;
  objectives: Array<
    | { kind: 'kill'; mob: string; count: number; mob_id: number }
    | { kind: 'collect'; item: string; count: number; item_id: number }
  >;
  rewards: Array<
    | { kind: 'gold'; copper: number }
    | { kind: 'item'; name: string; count: number; item_id: number }
  >;
  next_quest_id: string | null;
}

export interface VCreature {
  id: number;
  name: string;
  subname: string | null;
  min_level: number;
  max_level: number;
  faction: number;
  family: number;
  type: number;
  npc_flags: number;
  damage_mult: number;
  armor_mult: number;
  health_mult: number;
  unit_class: number;
  rank: number; // 0=normal 1=elite 2=rare-elite 3=boss 4=rare
}

export interface VSpawn {
  mob_id: number;
  map: number; // 0=Eastern Kingdoms, 1=Kalimdor
  x: number;
  y: number;
  z: number;
  respawn_s: number;
  name: string;
  level: number;
  rank: number;
}

export interface VItem {
  id: number;
  name: string;
  desc: string | null;
  class: number;
  subclass: number;
  req_level: number;
  inv_type: number; // 1=head, 3=shoulder, 5=chest, 6=waist, 7=legs, 8=feet, 13=mainhand, 14=shield, 15=ranged ...
  quality: number; // 0=poor 1=common 2=uncommon 3=rare 4=epic 5=legendary
  item_level: number;
  s1_type: number; s1_val: number;
  s2_type: number; s2_val: number;
  s3_type: number; s3_val: number;
  s4_type: number; s4_val: number;
  dmg_min: number; dmg_max: number; dmg_type: number;
  armor: number;
  buy_price: number; sell_price: number;
}

export interface VSpell {
  id: number;
  name: string;
  level: number;
  family: number;
  effect: number;
  effect_misc: number;
  base1: number; base2: number; base3: number;
  mechanic: number;
  mana_cost: number;
  cooldown_ms: number;
  cast_time: number;
  rank: string | null;
  dmg_class: number;
  school: number;
}

const BASE = `${import.meta.env.BASE_URL}data/vanilla/`;

const cache: Partial<Record<'quests' | 'creatures' | 'spawns' | 'items' | 'spells', unknown>> = {};

async function fetchJson<T>(name: string): Promise<T[]> {
  if (cache[name as keyof typeof cache]) return cache[name as keyof typeof cache] as T[];
  const r = await fetch(BASE + `vanilla-${name}.json`);
  if (!r.ok) throw new Error(`WotLK 3.3.5a data fetch failed: ${name} ${r.status}`);
  const data = await r.json();
  cache[name as keyof typeof cache] = data;
  return data as T[];
}

export const vanillaData = {
  quests:    () => fetchJson<VQuest>('quests'),
  creatures: () => fetchJson<VCreature>('creatures'),
  spawns:    () => fetchJson<VSpawn>('spawns'),
  items:     () => fetchJson<VItem>('items'),
  spells:    () => fetchJson<VSpell>('spells'),
};

// ── Indexes (memoised) ──────────────────────────────────────────────
let creatureById: Map<number, VCreature> | null = null;
let itemById: Map<number, VItem> | null = null;
let questsByZone: Map<string, VQuest[]> | null = null;
let spawnsByMap: Map<number, VSpawn[]> | null = null;

export async function getCreatureById(id: number): Promise<VCreature | null> {
  if (!creatureById) {
    const all = await vanillaData.creatures();
    creatureById = new Map(all.map(c => [c.id, c]));
  }
  return creatureById.get(id) ?? null;
}

export async function getItemById(id: number): Promise<VItem | null> {
  if (!itemById) {
    const all = await vanillaData.items();
    itemById = new Map(all.map(i => [i.id, i]));
  }
  return itemById.get(id) ?? null;
}

export async function getQuestsByZone(zoneSlug: string): Promise<VQuest[]> {
  if (!questsByZone) {
    const all = await vanillaData.quests();
    questsByZone = new Map();
    for (const q of all) {
      const arr = questsByZone.get(q.zone) ?? [];
      arr.push(q);
      questsByZone.set(q.zone, arr);
    }
  }
  return questsByZone.get(zoneSlug) ?? [];
}

export async function getSpawnsByMap(mapId: 0 | 1): Promise<VSpawn[]> {
  if (!spawnsByMap) {
    const all = await vanillaData.spawns();
    spawnsByMap = new Map();
    for (const s of all) {
      const arr = spawnsByMap.get(s.map) ?? [];
      arr.push(s);
      spawnsByMap.set(s.map, arr);
    }
  }
  return spawnsByMap.get(mapId) ?? [];
}

/**
 * Vanilla zone slug → AIWARI display name + level range.
 */
export const ZONE_INFO: Record<string, { name: string; minLevel: number; maxLevel: number; faction: 'alliance' | 'horde' | 'neutral' }> = {
  greenwald:      { name: 'Greenwald',         minLevel: 1,  maxLevel: 10, faction: 'alliance' },
  dunhammer:      { name: 'Dunhammer Reach',   minLevel: 1,  maxLevel: 10, faction: 'alliance' },
  worldroot:      { name: 'Worldroot',         minLevel: 1,  maxLevel: 10, faction: 'alliance' },
  hollow:         { name: 'Hollow Plains',     minLevel: 1,  maxLevel: 10, faction: 'horde' },
  mulhorn:        { name: 'Mulhorn Plains',    minLevel: 1,  maxLevel: 10, faction: 'horde' },
  redrock:        { name: 'Redrock Steppe',    minLevel: 1,  maxLevel: 10, faction: 'horde' },
  westmarch:      { name: 'Westmarch',         minLevel: 10, maxLevel: 20, faction: 'alliance' },
  lochmod:        { name: 'Loch Modran',       minLevel: 10, maxLevel: 20, faction: 'alliance' },
  darkwald:       { name: 'Darkwald',          minLevel: 10, maxLevel: 20, faction: 'alliance' },
  silverleaf:     { name: 'Silverleaf Pines',  minLevel: 10, maxLevel: 20, faction: 'horde' },
  barrens:        { name: 'The Wastelands',    minLevel: 10, maxLevel: 25, faction: 'horde' },
  redhills:       { name: 'Red Hills',         minLevel: 15, maxLevel: 25, faction: 'alliance' },
  twilightswamp:  { name: 'Twilight Swamp',    minLevel: 20, maxLevel: 30, faction: 'alliance' },
  gloomwood:      { name: 'Gloomwood',         minLevel: 18, maxLevel: 30, faction: 'alliance' },
  thousandwells:  { name: 'Thousand Wells',    minLevel: 25, maxLevel: 35, faction: 'horde' },
  stormveil:      { name: 'Stormveil Hollow',  minLevel: 15, maxLevel: 27, faction: 'neutral' },
  ashenvalley:    { name: 'Ashen Valley',      minLevel: 18, maxLevel: 30, faction: 'neutral' },
  arathih:        { name: 'Arathi Highlands',  minLevel: 30, maxLevel: 40, faction: 'neutral' },
  feralpath:      { name: 'Feral Path',        minLevel: 35, maxLevel: 45, faction: 'neutral' },
  azshira:        { name: 'Azshira',           minLevel: 45, maxLevel: 55, faction: 'neutral' },
  stranglehollow: { name: 'Stranglehollow Vale',minLevel: 30, maxLevel: 45, faction: 'neutral' },
  searinglands:   { name: 'Searing Lands',     minLevel: 45, maxLevel: 55, faction: 'neutral' },
  hillsplain:     { name: 'Hillsplain',        minLevel: 20, maxLevel: 30, faction: 'neutral' },
  swampsorrow:    { name: 'Swamp of Sorrow',   minLevel: 35, maxLevel: 45, faction: 'neutral' },
  hinterhills:    { name: 'Hinterhills',       minLevel: 40, maxLevel: 50, faction: 'neutral' },
  alterac:        { name: 'Alterac Mountains', minLevel: 30, maxLevel: 40, faction: 'neutral' },
  plagueforest:   { name: 'Plagueforest',      minLevel: 53, maxLevel: 60, faction: 'neutral' },
  westerplague:   { name: 'Western Plagueforest', minLevel: 51, maxLevel: 58, faction: 'neutral' },
  winterhold:     { name: 'Winterhold',        minLevel: 53, maxLevel: 60, faction: 'neutral' },
  ungoros:        { name: "Un'Goros Crater",   minLevel: 48, maxLevel: 55, faction: 'neutral' },
  felwoods:       { name: 'Fellwoods',         minLevel: 48, maxLevel: 55, faction: 'neutral' },
  burningplain:   { name: 'Burning Plains',    minLevel: 50, maxLevel: 58, faction: 'neutral' },
  silinthus:      { name: 'Silinthus Ravage',  minLevel: 55, maxLevel: 60, faction: 'neutral' },
  tanaris:        { name: 'Tanaris Wastes',    minLevel: 40, maxLevel: 50, faction: 'neutral' },
};
